import { useState, useEffect, useCallback, useRef } from 'react';

interface Props {
  onClose: () => void;
  darkMode: boolean;
}

const TUTORIAL_CHAPTERS = [
  {
    id: 1,
    title: 'Getting Started',
    duration: '2:15',
    icon: 'ri-play-circle-line',
    description: 'Overview of the Master Architect platform and its 14 integrated tools.',
    thumbnail: 'https://public.readdy.ai/ai/img_res/39633a40ea8fc02da95e19383bb5c7df.jpg',
    tag: 'Intro',
    tagColor: 'bg-teal-100 text-teal-700',
  },
  {
    id: 2,
    title: 'Floor Plan Generator',
    duration: '3:42',
    icon: 'ri-layout-grid-line',
    description: 'Configure bedrooms, bathrooms, kitchen types, and extras to auto-generate your floor plan.',
    thumbnail: 'https://readdy.ai/api/search-image?query=architectural%20floor%20plan%20blueprint%20grid%20layout%20rooms%20labeled%20clean%20white%20background%20professional%20design%20software%20top%20view%20modern%20house%20plan&width=640&height=360&seq=tut2&orientation=landscape',
    tag: 'Floor Plan',
    tagColor: 'bg-cyan-100 text-cyan-700',
  },
  {
    id: 3,
    title: 'AI Render Studio',
    duration: '4:10',
    icon: 'ri-magic-line',
    description: 'Transform sketches into photorealistic renders using Rendair AI and Rerender AI.',
    thumbnail: 'https://readdy.ai/api/search-image?query=photorealistic%20architectural%20rendering%20modern%20house%20exterior%20beautiful%20lighting%20sunset%20sky%20lush%20garden%20high%20quality%203D%20visualization%20professional%20render&width=640&height=360&seq=tut3&orientation=landscape',
    tag: 'AI Render',
    tagColor: 'bg-violet-100 text-violet-700',
  },
  {
    id: 4,
    title: 'AI Video Studio',
    duration: '3:28',
    icon: 'ri-video-line',
    description: 'Animate your designs into cinematic walkthroughs using Nim Video technology.',
    thumbnail: 'https://readdy.ai/api/search-image?query=cinematic%20architectural%20walkthrough%20animation%20modern%20interior%20living%20room%20camera%20motion%20film%20style%20professional%20video%20production%20design%20visualization&width=640&height=360&seq=tut4&orientation=landscape',
    tag: 'AI Video',
    tagColor: 'bg-rose-100 text-rose-700',
  },
  {
    id: 5,
    title: 'Space Planner',
    duration: '2:55',
    icon: 'ri-ruler-line',
    description: 'Optimize room layouts for natural light, circulation, and space efficiency with Qbiq AI.',
    thumbnail: 'https://readdy.ai/api/search-image?query=space%20planning%20diagram%20office%20layout%20color%20coded%20zones%20top%20view%20architectural%20plan%20optimization%20modern%20workspace%20design%20grid%20overlay&width=640&height=360&seq=tut5&orientation=landscape',
    tag: 'Space',
    tagColor: 'bg-amber-100 text-amber-700',
  },
  {
    id: 6,
    title: 'Collaboration & Presentation',
    duration: '3:05',
    icon: 'ri-team-line',
    description: 'Annotate designs with your team in real-time and assemble client presentation decks.',
    thumbnail: 'https://readdy.ai/api/search-image?query=team%20collaboration%20design%20review%20session%20architects%20pointing%20at%20screen%20annotations%20comments%20modern%20office%20professional%20meeting%20presentation%20deck&width=640&height=360&seq=tut6&orientation=landscape',
    tag: 'Collab',
    tagColor: 'bg-green-100 text-green-700',
  },
];

const FEATURE_HIGHLIGHTS = [
  { icon: 'ri-layout-grid-line', label: 'Floor Plan Generator', color: 'text-teal-500' },
  { icon: 'ri-magic-line', label: 'AI Render Studio', color: 'text-violet-500' },
  { icon: 'ri-video-line', label: 'AI Video Studio', color: 'text-rose-500' },
  { icon: 'ri-ruler-line', label: 'Space Planner', color: 'text-amber-500' },
  { icon: 'ri-team-line', label: 'Real-Time Collab', color: 'text-green-500' },
  { icon: 'ri-slideshow-line', label: 'Presentation Mode', color: 'text-cyan-500' },
  { icon: 'ri-file-list-3-line', label: 'Construction Docs', color: 'text-orange-500' },
  { icon: 'ri-links-line', label: '14 Software Integrations', color: 'text-indigo-500' },
];

const STORAGE_KEY = 'masterArchitectCompletedChapters';
const NOTES_KEY = 'masterArchitectChapterNotes';
const BOOKMARKS_KEY = 'masterArchitectChapterBookmarks';

// ── Bookmark type ────────────────────────────────────────────
interface Bookmark {
  id: string;
  chapterId: number;
  label: string;
  timestamp: string;
  createdAt: number;
}

// ── Confetti particle type ────────────────────────────────────────────
interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
}

const CONFETTI_COLORS = ['#14b8a6', '#06b6d4', '#f59e0b', '#10b981', '#f97316', '#8b5cf6', '#ec4899', '#ffffff'];

function ConfettiBurst({ onDone }: { onDone: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Spawn 180 particles from center-top
    particlesRef.current = Array.from({ length: 180 }, (_, i) => ({
      id: i,
      x: canvas.width / 2 + (Math.random() - 0.5) * 200,
      y: canvas.height * 0.35,
      vx: (Math.random() - 0.5) * 14,
      vy: Math.random() * -12 - 4,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: Math.random() * 8 + 4,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 8,
      opacity: 1,
    }));

    let frame = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach(p => {
        p.vy += 0.35; // gravity
        p.vx *= 0.99; // air resistance
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        if (frame > 60) p.opacity -= 0.018;

        ctx.save();
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      });

      frame++;
      if (frame < 140) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onDone();
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [onDone]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[9999]"
    />
  );
}

export default function TutorialModal({ onClose, darkMode }: Props) {
  const [activeChapter, setActiveChapter] = useState(TUTORIAL_CHAPTERS[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState<'chapters' | 'features' | 'progress' | 'notes'>('chapters');
  const [showConfetti, setShowConfetti] = useState(false);
  const [chapterSearch, setChapterSearch] = useState('');
  const prevCompletedCountRef = useRef(0);
  const [hoveredChapterId, setHoveredChapterId] = useState<number | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Onboarding checklist — persisted in localStorage
  const [completedChapters, setCompletedChapters] = useState<number[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Notes per chapter — persisted in localStorage
  const [chapterNotes, setChapterNotes] = useState<Record<number, string>>(() => {
    try {
      const saved = localStorage.getItem(NOTES_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Bookmarks — persisted in localStorage
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(() => {
    try {
      const saved = localStorage.getItem(BOOKMARKS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [editingBookmarkId, setEditingBookmarkId] = useState<string | null>(null);
  const [editingBookmarkLabel, setEditingBookmarkLabel] = useState('');

  const [showShortcutHint, setShowShortcutHint] = useState(true);

  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const hoverBg = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100';
  const activeBg = darkMode ? 'bg-gray-700 border-teal-500' : 'bg-teal-50 border-teal-400';

  const currentIndex = TUTORIAL_CHAPTERS.findIndex(c => c.id === activeChapter.id);
  const hasNext = currentIndex < TUTORIAL_CHAPTERS.length - 1;
  const hasPrev = currentIndex > 0;

  const markCompleted = useCallback((chapterId: number) => {
    setCompletedChapters(prev => {
      if (prev.includes(chapterId)) return prev;
      const updated = [...prev, chapterId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const toggleCompleted = useCallback((chapterId: number) => {
    setCompletedChapters(prev => {
      const updated = prev.includes(chapterId)
        ? prev.filter(id => id !== chapterId)
        : [...prev, chapterId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Fire confetti when all chapters become completed
  useEffect(() => {
    const prev = prevCompletedCountRef.current;
    const curr = completedChapters.length;
    if (prev < TUTORIAL_CHAPTERS.length && curr === TUTORIAL_CHAPTERS.length) {
      setShowConfetti(true);
    }
    prevCompletedCountRef.current = curr;
  }, [completedChapters]);

  const handleNoteChange = useCallback((chapterId: number, text: string) => {
    setChapterNotes(prev => {
      const updated = { ...prev, [chapterId]: text };
      localStorage.setItem(NOTES_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ── Bookmark handlers ────────────────────────────────────────────
  const addBookmark = useCallback(() => {
    // Simulate a timestamp (e.g., 1:23)
    const minutes = Math.floor(Math.random() * 3) + 1;
    const seconds = Math.floor(Math.random() * 60);
    const timestamp = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    
    const newBookmark: Bookmark = {
      id: `bm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      chapterId: activeChapter.id,
      label: `${activeChapter.title} @ ${timestamp}`,
      timestamp,
      createdAt: Date.now(),
    };

    setBookmarks(prev => {
      const updated = [...prev, newBookmark];
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, [activeChapter]);

  const deleteBookmark = useCallback((bookmarkId: string) => {
    setBookmarks(prev => {
      const updated = prev.filter(b => b.id !== bookmarkId);
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const startEditingBookmark = useCallback((bookmark: Bookmark) => {
    setEditingBookmarkId(bookmark.id);
    setEditingBookmarkLabel(bookmark.label);
  }, []);

  const saveBookmarkLabel = useCallback((bookmarkId: string) => {
    if (!editingBookmarkLabel.trim()) {
      setEditingBookmarkId(null);
      return;
    }

    setBookmarks(prev => {
      const updated = prev.map(b =>
        b.id === bookmarkId ? { ...b, label: editingBookmarkLabel.trim() } : b
      );
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(updated));
      return updated;
    });
    setEditingBookmarkId(null);
  }, [editingBookmarkLabel]);

  const cancelEditingBookmark = useCallback(() => {
    setEditingBookmarkId(null);
    setEditingBookmarkLabel('');
  }, []);

  const jumpToBookmark = useCallback((bookmark: Bookmark) => {
    const chapter = TUTORIAL_CHAPTERS.find(c => c.id === bookmark.chapterId);
    if (chapter) {
      setActiveChapter(chapter);
      setIsPlaying(false);
      setActiveTab('chapters');
    }
  }, []);

  const handleChapterClick = useCallback((chapter: typeof TUTORIAL_CHAPTERS[0]) => {
    setActiveChapter(chapter);
    setIsPlaying(false);
  }, []);

  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => {
      const next = !prev;
      if (next) markCompleted(activeChapter.id);
      return next;
    });
  }, [activeChapter.id, markCompleted]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (hasNext) handleChapterClick(TUTORIAL_CHAPTERS[currentIndex + 1]);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (hasPrev) handleChapterClick(TUTORIAL_CHAPTERS[currentIndex - 1]);
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handlePlayPause, handleChapterClick, hasNext, hasPrev, currentIndex, onClose]);

  useEffect(() => {
    const t = setTimeout(() => setShowShortcutHint(false), 4000);
    return () => clearTimeout(t);
  }, []);

  const handleExportNotes = () => {
    const hasAnyNote = Object.values(chapterNotes).some(n => n.trim().length > 0);
    if (!hasAnyNote) return;

    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const lines: string[] = [
      '═══════════════════════════════════════════════',
      '  MASTER ARCHITECT — Tutorial Chapter Notes',
      `  Exported: ${date}`,
      '═══════════════════════════════════════════════',
      '',
    ];

    TUTORIAL_CHAPTERS.forEach((chapter, idx) => {
      const note = (chapterNotes[chapter.id] || '').trim();
      if (!note) return;
      lines.push(`Chapter ${idx + 1}: ${chapter.title}  [${chapter.duration}]`);
      lines.push('─'.repeat(48));
      lines.push(note);
      lines.push('');
    });

    lines.push('═══════════════════════════════════════════════');
    lines.push('  Master Architect  |  masterarchitect.app');
    lines.push('═══════════════════════════════════════════════');

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'master-architect-notes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredChapters = TUTORIAL_CHAPTERS.filter(c =>
    c.title.toLowerCase().includes(chapterSearch.toLowerCase()) ||
    c.tag.toLowerCase().includes(chapterSearch.toLowerCase()) ||
    c.description.toLowerCase().includes(chapterSearch.toLowerCase())
  );

  const completedCount = completedChapters.length;
  const totalChapters = TUTORIAL_CHAPTERS.length;
  const progressPct = Math.round((completedCount / totalChapters) * 100);
  const notesCount = Object.values(chapterNotes).filter(n => n.trim().length > 0).length;
  const bookmarksCount = bookmarks.length;

  const handleChapterHoverEnter = useCallback((id: number) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHoveredChapterId(id), 300);
  }, []);

  const handleChapterHoverLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredChapterId(null);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  return (
    <>
      {/* Confetti overlay */}
      {showConfetti && <ConfettiBurst onDone={() => setShowConfetti(false)} />}

      {/* All-chapters-done celebration banner */}
      {showConfetti && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9998] pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 text-center border-2 border-teal-400 animate-bounce">
            <div className="text-5xl mb-3">🎉</div>
            <p className="text-2xl font-extrabold text-teal-600">All Chapters Done!</p>
            <p className="text-sm text-gray-500 mt-1">You&apos;ve completed the full Master Architect tutorial</p>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div
          className={`${bg} rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden border ${border} transition-colors duration-300`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-6 py-4 border-b ${border} flex-shrink-0`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <i className="ri-play-circle-line text-xl text-white"></i>
              </div>
              <div>
                <h2 className={`text-lg font-bold ${textPrimary}`}>Master Architect Tutorial</h2>
                <p className={`text-xs ${textSecondary}`}>6 chapters · ~19 min total</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className={`w-28 h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <span className={`text-xs font-semibold ${textSecondary}`}>{completedCount}/{totalChapters}</span>
              </div>
              <button
                onClick={onClose}
                className={`w-9 h-9 rounded-full ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'} flex items-center justify-center transition-colors cursor-pointer`}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left: Video Player */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Video Area */}
              <div className="relative bg-black flex-shrink-0" style={{ aspectRatio: '16/9', maxHeight: '360px' }}>
                <img
                  src={activeChapter.thumbnail}
                  alt={activeChapter.title}
                  className="w-full h-full object-cover object-top opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                <button
                  onClick={handlePlayPause}
                  className="absolute inset-0 flex items-center justify-center group cursor-pointer"
                >
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isPlaying
                      ? 'bg-white/20 group-hover:bg-white/30 scale-95'
                      : 'bg-white/90 group-hover:bg-white scale-100 shadow-2xl'
                  }`}>
                    <i className={`text-3xl ${isPlaying ? 'ri-pause-fill text-white' : 'ri-play-fill text-gray-900'}`}></i>
                  </div>
                </button>

                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${activeChapter.tagColor}`}>
                    {activeChapter.tag}
                  </span>
                  {completedChapters.includes(activeChapter.id) && (
                    <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-green-500 text-white flex items-center gap-1">
                      <i className="ri-check-line"></i> Completed
                    </span>
                  )}
                </div>

                <div className="absolute top-3 right-3">
                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-black/60 text-white">
                    <i className="ri-time-line mr-1"></i>{activeChapter.duration}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 px-4 py-3">
                  <p className="text-white font-semibold text-base">{activeChapter.title}</p>
                  <p className="text-white/70 text-xs mt-0.5">{activeChapter.description}</p>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                  <div
                    className="h-full bg-gradient-to-r from-teal-400 to-cyan-400 transition-all duration-300"
                    style={{ width: isPlaying ? '35%' : '0%' }}
                  ></div>
                </div>

                {showShortcutHint && (
                  <div className="absolute bottom-6 right-4 bg-black/70 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-2 animate-fade-in">
                    <i className="ri-keyboard-line"></i>
                    <span>Space to play · ← → to switch chapters</span>
                  </div>
                )}
              </div>

              {/* Video Controls */}
              <div className={`flex items-center justify-between px-4 py-3 border-b ${border} flex-shrink-0 ${cardBg}`}>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => hasPrev && handleChapterClick(TUTORIAL_CHAPTERS[currentIndex - 1])}
                    disabled={!hasPrev}
                    title="Previous chapter (←)"
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                      hasPrev
                        ? darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
                        : 'opacity-30 cursor-not-allowed ' + (darkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-400')
                    }`}
                  >
                    <i className="ri-skip-back-line text-sm"></i>
                  </button>
                  <button
                    onClick={handlePlayPause}
                    title="Play / Pause (Space)"
                    className="w-9 h-9 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 flex items-center justify-center text-white hover:shadow-md transition-all cursor-pointer"
                  >
                    <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-base`}></i>
                  </button>
                  <button
                    onClick={() => hasNext && handleChapterClick(TUTORIAL_CHAPTERS[currentIndex + 1])}
                    disabled={!hasNext}
                    title="Next chapter (→)"
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                      hasNext
                        ? darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
                        : 'opacity-30 cursor-not-allowed ' + (darkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-400')
                    }`}
                  >
                    <i className="ri-skip-forward-line text-sm"></i>
                  </button>
                  
                  {/* Bookmark button */}
                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                  <button
                    onClick={addBookmark}
                    title="Bookmark this moment"
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                      darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
                    }`}
                  >
                    <i className="ri-bookmark-line text-sm"></i>
                    Bookmark
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`text-xs ${textSecondary}`}>
                    Chapter {currentIndex + 1} of {TUTORIAL_CHAPTERS.length}
                  </span>
                  <div className="flex items-center gap-1">
                    {[
                      { key: 'Space', label: 'Play' },
                      { key: '←', label: 'Prev' },
                      { key: '→', label: 'Next' },
                    ].map(s => (
                      <span
                        key={s.key}
                        className={`px-1.5 py-0.5 rounded text-xs font-mono border ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-white border-gray-300 text-gray-500'}`}
                        title={s.label}
                      >
                        {s.key}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => markCompleted(activeChapter.id)}
                    title="Mark as completed"
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                      completedChapters.includes(activeChapter.id)
                        ? 'bg-green-100 text-green-700'
                        : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
                    }`}
                  >
                    <i className={completedChapters.includes(activeChapter.id) ? 'ri-checkbox-circle-fill' : 'ri-checkbox-circle-line'}></i>
                    {completedChapters.includes(activeChapter.id) ? 'Completed' : 'Mark Done'}
                  </button>
                  <button className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'}`}>
                    <i className="ri-volume-up-line text-sm"></i>
                  </button>
                  <button className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'}`}>
                    <i className="ri-fullscreen-line text-sm"></i>
                  </button>
                </div>
              </div>

              {/* Feature Highlights */}
              <div className="flex-1 overflow-y-auto p-4">
                <p className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-3`}>What you&apos;ll learn</p>
                <div className="grid grid-cols-2 gap-2">
                  {FEATURE_HIGHLIGHTS.map((f) => (
                    <div
                      key={f.label}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg ${cardBg} border ${border}`}
                    >
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        <i className={`${f.icon} text-base ${f.color}`}></i>
                      </div>
                      <span className={`text-xs font-medium ${textPrimary}`}>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Chapter List / Features / Progress / Notes */}
            <div className={`w-72 flex-shrink-0 border-l ${border} flex flex-col`}>
              {/* Tabs */}
              <div className={`flex border-b ${border} flex-shrink-0`}>
                {(['chapters', 'features', 'progress', 'notes'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-3 text-xs font-semibold capitalize transition-colors cursor-pointer relative ${
                      activeTab === tab
                        ? `${textPrimary} border-b-2 border-teal-500`
                        : `${textSecondary} ${hoverBg}`
                    }`}
                  >
                    {tab}
                    {tab === 'progress' && completedCount > 0 && (
                      <span className="absolute top-1.5 right-1 w-4 h-4 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center leading-none">
                        {completedCount}
                      </span>
                    )}
                    {tab === 'notes' && (notesCount > 0 || bookmarksCount > 0) && (
                      <span className="absolute top-1.5 right-1 w-4 h-4 bg-amber-400 text-white text-xs rounded-full flex items-center justify-center leading-none">
                        {notesCount + bookmarksCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto">
                {/* ── Chapters Tab ── */}
                {activeTab === 'chapters' && (
                  <div className="flex flex-col h-full">
                    {/* Search bar */}
                    <div className={`px-3 pt-3 pb-2 border-b ${border} flex-shrink-0`}>
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                        darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                      }`}>
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className={`ri-search-line text-sm ${textSecondary}`}></i>
                        </div>
                        <input
                          type="text"
                          value={chapterSearch}
                          onChange={e => setChapterSearch(e.target.value)}
                          placeholder="Search chapters…"
                          className={`flex-1 text-xs bg-transparent outline-none ${textPrimary} placeholder:${textSecondary}`}
                        />
                        {chapterSearch && (
                          <button
                            onClick={() => setChapterSearch('')}
                            className="w-4 h-4 flex items-center justify-center flex-shrink-0 cursor-pointer hover:text-red-400 transition-colors"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        )}
                      </div>
                      {chapterSearch && (
                        <p className={`text-xs mt-1.5 ${textSecondary}`}>
                          {filteredChapters.length === 0
                            ? 'No chapters match your search'
                            : `${filteredChapters.length} result${filteredChapters.length > 1 ? 's' : ''} found`}
                        </p>
                      )}
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {filteredChapters.length === 0 ? (
                        <div className={`text-center py-8 ${textSecondary}`}>
                          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                            <i className="ri-search-line text-2xl opacity-40"></i>
                          </div>
                          <p className="text-xs">No chapters found</p>
                          <button
                            onClick={() => setChapterSearch('')}
                            className="text-xs text-teal-500 hover:underline mt-1 cursor-pointer"
                          >
                            Clear search
                          </button>
                        </div>
                      ) : (
                        filteredChapters.map((chapter) => {
                          const idx = TUTORIAL_CHAPTERS.findIndex(c => c.id === chapter.id);
                          const isHovered = hoveredChapterId === chapter.id;
                          return (
                            <div
                              key={chapter.id}
                              className="relative"
                              onMouseEnter={() => handleChapterHoverEnter(chapter.id)}
                              onMouseLeave={handleChapterHoverLeave}
                            >
                              <button
                                onClick={() => handleChapterClick(chapter)}
                                className={`w-full text-left rounded-xl border-2 p-3 transition-all cursor-pointer ${
                                  activeChapter.id === chapter.id
                                    ? activeBg
                                    : `border-transparent ${cardBg} ${hoverBg}`
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                                    completedChapters.includes(chapter.id)
                                      ? 'bg-green-500 text-white'
                                      : activeChapter.id === chapter.id
                                      ? 'bg-teal-500 text-white'
                                      : darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                                  }`}>
                                    {completedChapters.includes(chapter.id)
                                      ? <i className="ri-check-line text-xs"></i>
                                      : idx + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-semibold ${textPrimary} leading-tight mb-1`}>{chapter.title}</p>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-xs ${textSecondary}`}>
                                        <i className="ri-time-line mr-0.5"></i>{chapter.duration}
                                      </span>
                                      <span className={`px-1.5 py-0.5 text-xs rounded-full ${chapter.tagColor}`}>
                                        {chapter.tag}
                                      </span>
                                    </div>
                                  </div>
                                  {activeChapter.id === chapter.id && (
                                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                      <i className="ri-play-circle-fill text-teal-500 text-lg"></i>
                                    </div>
                                  )}
                                </div>
                              </button>

                              {/* Hover tooltip / expandable preview */}
                              {isHovered && activeChapter.id !== chapter.id && (
                                <div
                                  className={`absolute left-full top-0 ml-2 w-56 z-50 rounded-xl shadow-2xl border overflow-hidden pointer-events-none ${
                                    darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                                  }`}
                                  style={{ minWidth: '220px' }}
                                >
                                  {/* Thumbnail */}
                                  <div className="relative w-full h-28 overflow-hidden">
                                    <img
                                      src={chapter.thumbnail}
                                      alt={chapter.title}
                                      className="w-full h-full object-cover object-top"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                    <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${chapter.tagColor}`}>
                                        {chapter.tag}
                                      </span>
                                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-black/60 text-white">
                                        <i className="ri-time-line mr-0.5"></i>{chapter.duration}
                                      </span>
                                    </div>
                                  </div>
                                  {/* Info */}
                                  <div className="p-3">
                                    <p className={`text-xs font-bold ${textPrimary} mb-1`}>
                                      Chapter {idx + 1}: {chapter.title}
                                    </p>
                                    <p className={`text-xs leading-relaxed ${textSecondary}`}>
                                      {chapter.description}
                                    </p>
                                    {completedChapters.includes(chapter.id) && (
                                      <div className="mt-2 flex items-center gap-1 text-green-500 text-xs font-semibold">
                                        <i className="ri-checkbox-circle-fill"></i>
                                        Completed
                                      </div>
                                    )}
                                    <div className={`mt-2 pt-2 border-t ${border} flex items-center gap-1 text-xs ${textSecondary}`}>
                                      <i className="ri-cursor-line text-teal-500"></i>
                                      Click to watch
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* ── Features Tab ── */}
                {activeTab === 'features' && (
                  <div className="p-3 space-y-2">
                    {[
                      { icon: 'ri-magic-line', title: 'Smart Wizard', desc: 'Auto-configure your floor plan in 4 steps', color: 'text-teal-500' },
                      { icon: 'ri-save-line', title: 'Save Projects', desc: 'Save and reload your design configurations', color: 'text-cyan-500' },
                      { icon: 'ri-moon-line', title: 'Dark / Light Mode', desc: 'Switch themes for comfortable working', color: 'text-indigo-500' },
                      { icon: 'ri-fullscreen-line', title: 'Fullscreen Preview', desc: 'Showcase your canvas in full screen', color: 'text-violet-500' },
                      { icon: 'ri-share-line', title: 'Share & Export', desc: 'Share designs with clients instantly', color: 'text-rose-500' },
                      { icon: 'ri-history-line', title: 'Version History', desc: 'Restore any previous design snapshot', color: 'text-amber-500' },
                    ].map((feat) => (
                      <div key={feat.title} className={`rounded-xl p-3 ${cardBg} border ${border}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                            <i className={`${feat.icon} text-lg ${feat.color}`}></i>
                          </div>
                          <div>
                            <p className={`text-xs font-semibold ${textPrimary} mb-0.5`}>{feat.title}</p>
                            <p className={`text-xs ${textSecondary} leading-relaxed`}>{feat.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Progress Tab ── */}
                {activeTab === 'progress' && (
                  <div className="p-4 space-y-4">
                    <div className={`rounded-xl p-4 ${darkMode ? 'bg-gray-800' : 'bg-gradient-to-br from-teal-50 to-cyan-50'} border ${border}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-bold ${textPrimary}`}>Your Progress</span>
                        <span className="text-sm font-bold text-teal-600">{progressPct}%</span>
                      </div>
                      <div className={`w-full h-3 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                        <div
                          className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full transition-all duration-700"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <p className={`text-xs mt-2 ${textSecondary}`}>
                        {completedCount === 0 && 'Start watching to track your progress!'}
                        {completedCount > 0 && completedCount < totalChapters && `${totalChapters - completedCount} chapter${totalChapters - completedCount > 1 ? 's' : ''} remaining`}
                        {completedCount === totalChapters && '🎉 All chapters completed!'}
                      </p>
                    </div>

                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-3`}>Chapter Checklist</p>
                      <div className="space-y-2">
                        {TUTORIAL_CHAPTERS.map((chapter, idx) => {
                          const done = completedChapters.includes(chapter.id);
                          return (
                            <div
                              key={chapter.id}
                              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                                done
                                  ? darkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'
                                  : `${cardBg} border-transparent`
                              }`}
                            >
                              <button
                                onClick={() => toggleCompleted(chapter.id)}
                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                                  done
                                    ? 'bg-green-500 border-green-500 text-white'
                                    : darkMode ? 'border-gray-600 hover:border-teal-400' : 'border-gray-300 hover:border-teal-400'
                                }`}
                              >
                                {done && <i className="ri-check-line text-xs"></i>}
                              </button>
                              <button onClick={() => handleChapterClick(chapter)} className="flex-1 text-left cursor-pointer">
                                <p className={`text-xs font-semibold ${done ? 'line-through opacity-60' : ''} ${textPrimary}`}>
                                  {idx + 1}. {chapter.title}
                                </p>
                                <p className={`text-xs ${textSecondary} mt-0.5`}>{chapter.duration}</p>
                              </button>
                              <span className={`px-1.5 py-0.5 text-xs rounded-full ${chapter.tagColor} flex-shrink-0`}>
                                {chapter.tag}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {completedCount > 0 && (
                      <button
                        onClick={() => {
                          setCompletedChapters([]);
                          localStorage.removeItem(STORAGE_KEY);
                        }}
                        className={`w-full py-2 text-xs font-medium rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                          darkMode
                            ? 'border-gray-600 text-gray-400 hover:bg-gray-700'
                            : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <i className="ri-refresh-line mr-1"></i>Reset Progress
                      </button>
                    )}
                  </div>
                )}

                {/* ── Notes Tab ── */}
                {activeTab === 'notes' && (
                  <div className="p-3 flex flex-col gap-3 h-full">
                    {/* Chapter selector */}
                    <div className="space-y-1.5">
                      {TUTORIAL_CHAPTERS.map((chapter, idx) => {
                        const hasNote = (chapterNotes[chapter.id] || '').trim().length > 0;
                        const chapterBookmarks = bookmarks.filter(b => b.chapterId === chapter.id);
                        const hasBookmark = chapterBookmarks.length > 0;
                        const isActive = activeChapter.id === chapter.id;
                        return (
                          <button
                            key={chapter.id}
                            onClick={() => handleChapterClick(chapter)}
                            className={`w-full text-left px-3 py-2 rounded-lg border transition-all cursor-pointer flex items-center gap-2 ${
                              isActive
                                ? darkMode ? 'bg-gray-700 border-teal-500' : 'bg-teal-50 border-teal-400'
                                : `border-transparent ${cardBg} ${hoverBg}`
                            }`}
                          >
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              isActive ? 'bg-teal-500 text-white' : darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                            }`}>{idx + 1}</span>
                            <span className={`text-xs font-medium flex-1 truncate ${textPrimary}`}>{chapter.title}</span>
                            <div className="flex items-center gap-1">
                              {hasNote && (
                                <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title="Has notes"></span>
                              )}
                              {hasBookmark && (
                                <span className={`text-xs ${textSecondary} flex-shrink-0`} title={`${chapterBookmarks.length} bookmark${chapterBookmarks.length > 1 ? 's' : ''}`}>
                                  <i className="ri-bookmark-fill text-teal-500"></i>
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Bookmarks section for active chapter */}
                    {bookmarks.filter(b => b.chapterId === activeChapter.id).length > 0 && (
                      <div className={`rounded-xl border ${border} overflow-hidden`}>
                        <div className={`px-3 py-2 border-b ${border} ${cardBg} flex items-center justify-between`}>
                          <span className={`text-xs font-semibold ${textPrimary} flex items-center gap-1.5`}>
                            <i className="ri-bookmark-line text-teal-500"></i>
                            Bookmarks
                          </span>
                          <span className={`text-xs ${textSecondary}`}>
                            {bookmarks.filter(b => b.chapterId === activeChapter.id).length}
                          </span>
                        </div>
                        <div className="p-2 space-y-1.5 max-h-32 overflow-y-auto">
                          {bookmarks
                            .filter(b => b.chapterId === activeChapter.id)
                            .sort((a, b) => b.createdAt - a.createdAt)
                            .map(bookmark => (
                              <div
                                key={bookmark.id}
                                className={`flex items-center gap-2 p-2 rounded-lg ${cardBg} border ${border}`}
                              >
                                {editingBookmarkId === bookmark.id ? (
                                  <>
                                    <input
                                      type="text"
                                      value={editingBookmarkLabel}
                                      onChange={e => setEditingBookmarkLabel(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') saveBookmarkLabel(bookmark.id);
                                        if (e.key === 'Escape') cancelEditingBookmark();
                                      }}
                                      className={`flex-1 text-xs px-2 py-1 rounded border outline-none ${
                                        darkMode
                                          ? 'bg-gray-700 border-gray-600 text-gray-200'
                                          : 'bg-white border-gray-300 text-gray-800'
                                      }`}
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => saveBookmarkLabel(bookmark.id)}
                                      className="w-6 h-6 flex items-center justify-center text-green-500 hover:bg-green-100 dark:hover:bg-green-900/30 rounded cursor-pointer flex-shrink-0"
                                      title="Save"
                                    >
                                      <i className="ri-check-line text-sm"></i>
                                    </button>
                                    <button
                                      onClick={cancelEditingBookmark}
                                      className="w-6 h-6 flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer flex-shrink-0"
                                      title="Cancel"
                                    >
                                      <i className="ri-close-line text-sm"></i>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => jumpToBookmark(bookmark)}
                                      className="flex-1 text-left cursor-pointer group"
                                      title="Jump to this chapter"
                                    >
                                      <p className={`text-xs font-medium ${textPrimary} group-hover:text-teal-500 transition-colors truncate`}>
                                        {bookmark.label}
                                      </p>
                                      <p className={`text-xs ${textSecondary} mt-0.5`}>
                                        <i className="ri-time-line mr-0.5"></i>{bookmark.timestamp}
                                      </p>
                                    </button>
                                    <button
                                      onClick={() => startEditingBookmark(bookmark)}
                                      className={`w-6 h-6 flex items-center justify-center ${textSecondary} hover:text-teal-500 rounded cursor-pointer flex-shrink-0`}
                                      title="Edit label"
                                    >
                                      <i className="ri-edit-line text-xs"></i>
                                    </button>
                                    <button
                                      onClick={() => deleteBookmark(bookmark.id)}
                                      className={`w-6 h-6 flex items-center justify-center ${textSecondary} hover:text-red-500 rounded cursor-pointer flex-shrink-0`}
                                      title="Delete bookmark"
                                    >
                                      <i className="ri-delete-bin-line text-xs"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Note editor for active chapter */}
                    <div className={`flex-1 flex flex-col rounded-xl border ${border} overflow-hidden`}>
                      <div className={`px-3 py-2 border-b ${border} flex items-center justify-between ${cardBg}`}>
                        <span className={`text-xs font-semibold ${textPrimary} flex items-center gap-1.5`}>
                          <i className="ri-sticky-note-line text-amber-400"></i>
                          Notes — {activeChapter.title}
                        </span>
                        {(chapterNotes[activeChapter.id] || '').trim().length > 0 && (
                          <button
                            onClick={() => handleNoteChange(activeChapter.id, '')}
                            className={`text-xs ${textSecondary} hover:text-red-500 transition-colors cursor-pointer`}
                            title="Clear note"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        )}
                      </div>
                      <textarea
                        value={chapterNotes[activeChapter.id] || ''}
                        onChange={e => handleNoteChange(activeChapter.id, e.target.value)}
                        placeholder={`Jot down your notes for "${activeChapter.title}"…`}
                        maxLength={1000}
                        className={`flex-1 w-full p-3 text-xs resize-none outline-none leading-relaxed ${
                          darkMode
                            ? 'bg-gray-800 text-gray-200 placeholder-gray-600'
                            : 'bg-white text-gray-800 placeholder-gray-400'
                        }`}
                        style={{ minHeight: '120px' }}
                      />
                      <div className={`px-3 py-1.5 border-t ${border} flex items-center justify-between ${cardBg}`}>
                        <span className={`text-xs ${textSecondary}`}>
                          {(chapterNotes[activeChapter.id] || '').length}/1000
                        </span>
                        <span className={`text-xs ${textSecondary} flex items-center gap-1`}>
                          <i className="ri-save-line text-teal-500"></i> Auto-saved
                        </span>
                      </div>
                    </div>

                    {/* Export all notes button */}
                    {notesCount > 0 && (
                      <div className={`rounded-lg p-2.5 ${darkMode ? 'bg-amber-900/20 border border-amber-700/40' : 'bg-amber-50 border border-amber-200'}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className={`text-xs font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-700'} flex items-center gap-1`}>
                            <i className="ri-sticky-note-line"></i>
                            {notesCount} chapter{notesCount > 1 ? 's' : ''} with notes
                          </p>
                          <button
                            onClick={handleExportNotes}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                              darkMode
                                ? 'bg-amber-700/50 hover:bg-amber-700 text-amber-200'
                                : 'bg-amber-500 hover:bg-amber-600 text-white'
                            }`}
                            title="Download all notes as a .txt file"
                          >
                            <i className="ri-download-line"></i>
                            Export Notes
                          </button>
                        </div>
                        <p className={`text-xs ${darkMode ? 'text-amber-400/70' : 'text-amber-600/80'}`}>
                          Notes are saved locally in your browser.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* CTA */}
              <div className={`p-4 border-t ${border} flex-shrink-0`}>
                <button
                  onClick={onClose}
                  className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl font-semibold text-sm transition-all hover:shadow-lg cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-rocket-line mr-2"></i>Start Designing Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
